import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import type { Icon } from '@phosphor-icons/react';
import {
  FileCsv,
  FileHtml,
  Folder as FolderIcon,
  FolderLock,
  ImagesSquare,
  Playlist,
} from '@phosphor-icons/react';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAPIErrorMessage } from '../../../../helpers/helpers';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { useSidebarData } from '../../../../hooks/useSidebarData';
import { CompanySpacesService } from '../../../../service/CompanySpacesService';
import { UserService } from '../../../../service/UserService';
import { useAuthStore } from '../../../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useObjectStore } from '../../../../store/storeObjects';
import type { GeneratedAudio } from '../../../../types/generatedAudio';
import type { GeneratedFile } from '../../../../types/generatedFile';
import type { GeneratedImage } from '../../../../types/generatedImage';
import { Namespace } from '../../../../types/namespace';
import { Folder } from '../../../../util/Types/Folder';
import { UserObject } from '../../../../util/Types/User';
import { SidebarAddButton } from '../AgentsSidebar/SidebarAddButton';
import { FluxObject } from '../AgentsSidebar/context';
import { useExtendedSidebarContext } from '../ExtendedSidebar';
import { AnimatedListItem } from '../dnd/AnimatedListItem';
import { DragDropProvider } from '../dnd/DragDropProvider';
import { getReservedFolderType, isReservedFolder } from '../dnd/dropRules';
import { useSidebarAutoScroll } from '../dnd/useSidebarAutoScroll';
import { CollapsibleGroup } from '../shared/CollapsibleGroup';
import { useSidebarFolderActions } from '../shared/useSidebarFolderActions';
import { useSidebarRefresh } from '../shared/useSidebarRefresh';
import { useSidebarSpaceActions } from '../shared/useSidebarSpaceActions';
import { useVectorMemoryDeletion } from '../shared/useVectorMemoryDeletion';
import type { CreateItemType } from '../types';
import { enrichFoldersWithItems } from '../util/enrichFoldersWithItems';
import { DataFolderItem } from './DataFolderItem';
import { DataSidebarModals } from './DataSidebarModals';
import { GeneratedAudioItem } from './GeneratedAudioItem';
import { GeneratedFileItem } from './GeneratedFileItem';
import { GeneratedImageItem } from './GeneratedImageItem';
import { ObjectItem } from './ObjectItem';
import { Section } from './Section';
import { UploadItems } from './UploadItems';
import { VectorMemoryItem } from './VectorMemoryItem';
import { categorizeGeneratedFiles } from './categorizeFiles';
import { DataSidebarContext, DataSidebarContextValue } from './context';
import { useAssetHandlers } from './hooks/useAssetHandlers';
import { useGeneratedAssetsQueries } from './hooks/useGeneratedAssetsQueries';

interface OrganizedItems {
  objects: FluxObject[];
  vectorMemory: Namespace[];
  generatedImages: GeneratedImage[];
  generatedAudio: GeneratedAudio[];
  generatedFiles: GeneratedFile[];
}

interface FolderConfig {
  title: string;
  icon: Icon;
  images?: GeneratedImage[];
  audios?: GeneratedAudio[];
  files?: GeneratedFile[];
}

function filterBySearch(items: OrganizedItems, term: string): OrganizedItems {
  const lower = term.trim().toLowerCase();
  if (!lower) return items;
  return {
    objects: items.objects.filter(o => o.name?.toLowerCase().includes(lower)),
    vectorMemory: items.vectorMemory.filter(ns =>
      ns.name?.toLowerCase().includes(lower),
    ),
    generatedImages: items.generatedImages.filter(
      img =>
        img.provider?.toLowerCase().includes(lower) ||
        img.model?.toLowerCase().includes(lower),
    ),
    generatedAudio: items.generatedAudio.filter(
      audio =>
        audio.provider?.toLowerCase().includes(lower) ||
        audio.model?.toLowerCase().includes(lower),
    ),
    generatedFiles: items.generatedFiles.filter(
      file =>
        file.name?.toLowerCase().includes(lower) ||
        file.provider?.toLowerCase().includes(lower) ||
        file.fileType?.toLowerCase().includes(lower),
    ),
  };
}

interface ItemsSectionProps {
  items: OrganizedItems;
  spaceId?: string;
  customFolders?: Folder[];
  onObjectEdit: (obj: FluxObject) => void;
  onObjectDelete: (obj: FluxObject) => void;
  onObjectAdd: (spaceId?: string) => void;
  onVectorEdit: (ns: Namespace) => void;
  onVectorDelete: (ns: Namespace) => void;
  onVectorAdd: (spaceId?: string) => void;
  onImagePreview: (image: GeneratedImage) => void;
  onImageRename: (image: GeneratedImage) => void;
  onImageDelete: (image: GeneratedImage) => void;
  onAudioPreview: (audio: GeneratedAudio) => void;
  onAudioRename: (audio: GeneratedAudio) => void;
  onAudioDelete: (audio: GeneratedAudio) => void;
  onFilePreview: (file: GeneratedFile) => void;
  onFileRename: (file: GeneratedFile) => void;
  onFileDelete: (file: GeneratedFile) => void;
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
  onAddSubfolder?: (folder: Folder) => void;
  onAddItem?: (folder: Folder) => void;
}

const folderHasDataItems = (folder: Folder): boolean => {
  if ((folder.objects?.length ?? 0) > 0) return true;
  if ((folder.namespaces?.length ?? 0) > 0) return true;
  return (folder.folders ?? []).some(folderHasDataItems);
};

const ItemsSection: React.FC<ItemsSectionProps> = ({
  items,
  spaceId,
  customFolders,
  onObjectEdit,
  onObjectDelete,
  onObjectAdd,
  onVectorEdit,
  onVectorDelete,
  onVectorAdd,
  onImagePreview,
  onImageRename,
  onImageDelete,
  onAudioPreview,
  onAudioRename,
  onAudioDelete,
  onFilePreview,
  onFileRename,
  onFileDelete,
  onFolderRename,
  onFolderDelete,
  onAddSubfolder,
  onAddItem,
}) => {
  const { docs, pdfs, csvFiles, htmlFiles, imageFiles, others } =
    categorizeGeneratedFiles(items.generatedFiles);

  const folders: FolderConfig[] = [
    { title: 'Audios', icon: Playlist, audios: items.generatedAudio },
    { title: 'Docs', icon: FolderIcon, files: docs },
    {
      title: 'Images',
      icon: ImagesSquare,
      images: items.generatedImages,
      files: imageFiles,
    },
    { title: 'PDFs', icon: FolderIcon, files: pdfs },
    { title: 'CSV', icon: FileCsv, files: csvFiles },
    { title: 'HTML', icon: FileHtml, files: htmlFiles },
    { title: 'Others', icon: FolderIcon, files: others },
  ];

  const nonEmptyFolders = folders.filter(
    f =>
      (f.images?.length ?? 0) +
        (f.audios?.length ?? 0) +
        (f.files?.length ?? 0) >
      0,
  );

  const rootedObjects = items.objects.filter(o => o.folderId == null);
  const rootedVectorMemory = items.vectorMemory.filter(n => n.folderId == null);
  const dropSpaceId = spaceId ?? null;
  const visibleCustomFolders = (customFolders ?? []).filter(
    f => !isReservedFolder(f) && f.section === 'data',
  );

  return (
    <>
      {rootedObjects.length > 0 && (
        <CollapsibleGroup
          title='Objects'
          icon={FolderLock}
          action={<SidebarAddButton onClick={() => onObjectAdd(spaceId)} />}
        >
          <AnimatePresence initial={false}>
            {rootedObjects.map(obj => (
              <AnimatedListItem key={`object:${obj.id}`} itemKey={obj.id}>
                <ObjectItem
                  object={obj}
                  spaceId={dropSpaceId}
                  ancestorReservedType='objects'
                  onEdit={onObjectEdit}
                  onDelete={onObjectDelete}
                  isNested
                />
              </AnimatedListItem>
            ))}
          </AnimatePresence>
        </CollapsibleGroup>
      )}
      {rootedVectorMemory.length > 0 && (
        <CollapsibleGroup
          title='Vector Memory'
          icon={FolderLock}
          action={<SidebarAddButton onClick={() => onVectorAdd(spaceId)} />}
        >
          <AnimatePresence initial={false}>
            {rootedVectorMemory.map(ns => (
              <AnimatedListItem key={`ns:${ns.id}`} itemKey={ns.id}>
                <VectorMemoryItem
                  namespace={ns}
                  spaceId={dropSpaceId}
                  ancestorReservedType='vector memory'
                  onEdit={onVectorEdit}
                  onDelete={onVectorDelete}
                  isNested
                />
              </AnimatedListItem>
            ))}
          </AnimatePresence>
        </CollapsibleGroup>
      )}
      {nonEmptyFolders.length > 0 && (
        <CollapsibleGroup title='Generated Files' icon={FolderLock}>
          {nonEmptyFolders.map(folder => (
            <CollapsibleGroup
              key={folder.title}
              title={folder.title}
              icon={folder.icon}
            >
              {folder.images?.map(image => (
                <GeneratedImageItem
                  key={image.id}
                  image={image}
                  onPreview={onImagePreview}
                  onRename={onImageRename}
                  onDelete={onImageDelete}
                  isNested
                />
              ))}
              {folder.audios?.map(audio => (
                <GeneratedAudioItem
                  key={audio.id}
                  audio={audio}
                  onPreview={onAudioPreview}
                  onRename={onAudioRename}
                  onDelete={onAudioDelete}
                  isNested
                />
              ))}
              {folder.files?.map(file => (
                <GeneratedFileItem
                  key={file.id}
                  file={file}
                  onPreview={onFilePreview}
                  onRename={onFileRename}
                  onDelete={onFileDelete}
                  isNested
                />
              ))}
            </CollapsibleGroup>
          ))}
        </CollapsibleGroup>
      )}
      <AnimatePresence initial={false}>
        {visibleCustomFolders.map(folder => (
          <AnimatedListItem key={`folder:${folder.id}`} itemKey={folder.id}>
            <DataFolderItem
              folder={folder}
              spaceId={dropSpaceId}
              onObjectEdit={onObjectEdit}
              onObjectDelete={onObjectDelete}
              onVectorEdit={onVectorEdit}
              onVectorDelete={onVectorDelete}
              onFolderRename={onFolderRename}
              onFolderDelete={onFolderDelete}
              onAddSubfolder={onAddSubfolder}
              onAddItem={onAddItem}
            />
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </>
  );
};

export const DataSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const setScrollRef = useSidebarAutoScroll();

  const { searchTerm } = useExtendedSidebarContext();
  const { user: authUser, setAuthUser } = useAuthStore(state => state);
  const {
    setObjectUpdate,
    resetObjectUpdate,
    removeObject,
    setObjects,
    objects: objectStore,
  } = useObjectStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    folders: privateSidebarFolders,
    spaces,
    user: sidebarUser,
    isLoading,
    refresh,
    refreshTree,
    updateSpace,
  } = useSidebarData();

  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [editingObject, setEditingObject] = useState<FluxObject | null>(null);
  const [editingVector, setEditingVector] = useState<Namespace | null>(null);
  const [pendingVectorNamespace, setPendingVectorNamespace] =
    useState<Namespace | null>(null);
  const [createItemRequest, setCreateItemRequest] = useState<{
    type: CreateItemType;
    folderId?: string;
    spaceId?: string;
  } | null>(null);
  const [vectorCreateRequest, setVectorCreateRequest] = useState<{
    spaceId?: string;
  } | null>(null);

  const folderActions = useSidebarFolderActions({
    section: 'data',
    refresh,
  });
  const { handleAddFolder, handleFolderRename } = folderActions;

  const handleAddObject = useCallback((spaceId?: string) => {
    setCreateItemRequest({ type: 'object', spaceId });
  }, []);

  const handleAddVector = useCallback((spaceId?: string) => {
    setVectorCreateRequest({ spaceId });
  }, []);

  const handleAddItem = useCallback((folder: Folder) => {
    const reservedType = getReservedFolderType(folder);
    const spaceId = folder.space || undefined;
    if (reservedType === 'vector memory') {
      setVectorCreateRequest({ spaceId });
      return;
    }
    if (reservedType === 'objects') {
      setCreateItemRequest({ type: 'object', spaceId });
      return;
    }
    setCreateItemRequest({ type: 'object', folderId: folder.id, spaceId });
  }, []);

  const {
    privateImages,
    privateAudio,
    privateFiles,
    spaceImageResults,
    spaceAudioResults,
    spaceFileResults,
    deleteGeneratedImage,
    deleteGeneratedAudio,
    deleteGeneratedFile,
    queryClient,
  } = useGeneratedAssetsQueries(spaces, refresh);

  const { modals, dispatch, handlers, renameModal } = useAssetHandlers(
    queryClient,
    { deleteGeneratedImage, deleteGeneratedAudio, deleteGeneratedFile },
  );

  const { handleEditSpace, handleArchiveSpace } =
    useSidebarSpaceActions(refresh);

  const { deleteVectorMemory } = useVectorMemoryDeletion({
    spaces,
    updateSpace,
    refreshSidebar: refresh,
  });

  useEffect(() => {
    if (sidebarUser) setAuthUser(sidebarUser);
  }, [sidebarUser, setAuthUser]);

  useSidebarRefresh(refreshTree);

  const privateItems = useMemo<OrganizedItems>(
    () => ({
      objects: authUser?.objects ?? objectStore['user'] ?? [],
      vectorMemory: (authUser?.namespaces as Namespace[]) || [],
      generatedImages: privateImages,
      generatedAudio: privateAudio,
      generatedFiles: privateFiles,
    }),
    [
      objectStore,
      authUser?.objects,
      authUser?.namespaces,
      privateImages,
      privateAudio,
      privateFiles,
    ],
  );

  const enrichedPrivateFolders = useMemo(
    () =>
      enrichFoldersWithItems(
        privateSidebarFolders,
        privateItems.objects,
        privateItems.vectorMemory,
        null,
      ),
    [privateSidebarFolders, privateItems.objects, privateItems.vectorMemory],
  );

  const spaceItems = useMemo(
    () =>
      spaces.map((space, index) => ({
        space,
        items: {
          objects: space.objects?.length
            ? space.objects
            : objectStore[space.id] || [],
          vectorMemory: space.namespaces || [],
          generatedImages: spaceImageResults[index]?.data?.data ?? [],
          generatedAudio: spaceAudioResults[index]?.data?.data ?? [],
          generatedFiles: spaceFileResults[index]?.data?.data ?? [],
        } as OrganizedItems,
      })),
    [
      spaces,
      objectStore,
      spaceImageResults,
      spaceAudioResults,
      spaceFileResults,
    ],
  );

  const sortedSpaces = useMemo(
    () =>
      [...spaceItems].sort(
        (a, b) => Number(!!a.space.disabled) - Number(!!b.space.disabled),
      ),
    [spaceItems],
  );

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const all: OrganizedItems = {
      objects: [
        privateItems.objects,
        ...spaceItems.map(s => s.items.objects),
      ].flat(),
      vectorMemory: [
        privateItems.vectorMemory,
        ...spaceItems.map(s => s.items.vectorMemory),
      ].flat(),
      generatedImages: [
        privateItems.generatedImages,
        ...spaceItems.map(s => s.items.generatedImages),
      ].flat(),
      generatedAudio: [
        privateItems.generatedAudio,
        ...spaceItems.map(s => s.items.generatedAudio),
      ].flat(),
      generatedFiles: [
        privateItems.generatedFiles,
        ...spaceItems.map(s => s.items.generatedFiles),
      ].flat(),
    };
    return filterBySearch(all, searchTerm);
  }, [searchTerm, privateItems, spaceItems]);

  const handleObjectEdit = useCallback(
    (obj: FluxObject) => {
      const full =
        (authUser?.objects ?? []).find(o => o.id === obj.id) ??
        (spaces.flatMap(s => s.objects ?? []).find(o => o.id === obj.id) as
          | UserObject
          | undefined);
      if (!full) return;
      setObjectUpdate({
        isCreating: false,
        id: full.id,
        name: full.name,
        text: full.text,
        sessions: full.sessions,
        localObject: full.localObject,
        sessionEnabled: full.sessionEnabled,
        flowId: full.flowId,
      });
      setEditingObject(full as FluxObject);
    },
    [authUser?.objects, spaces, setObjectUpdate],
  );

  const handleObjectDelete = useCallback(
    async (obj: FluxObject) => {
      if (!window.confirm('Are you sure you want to delete this object?'))
        return;
      const spaceId = spaces.find(s =>
        s.objects?.some(o => o.id === obj.id),
      )?.id;
      removeObject(obj.id, spaceId ?? 'user');
      try {
        if (spaceId) {
          const space = spaces.find(s => s.id === spaceId);
          const updated = (space?.objects ?? []).filter(o => o.id !== obj.id);
          await CompanySpacesService.updateCompanySpace(
            authUser?.selectedCompanyId!,
            { spaceId, objects: updated },
          );
          setObjects(updated as UserObject[], spaceId);
        } else {
          const updated = (authUser?.objects ?? []).filter(
            o => o.id !== obj.id,
          );
          await UserService.updateUser({ objects: updated });
          setObjects(updated, 'user');
          refresh();
        }
        setNotification({
          type: NotificationType.Success,
          message: 'Object deleted successfully',
        });
      } catch (error) {
        refresh();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    },
    [authUser, spaces, removeObject, setObjects, refresh, setNotification],
  );

  if (isLoading && spaces.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const contextValue = useMemo<DataSidebarContextValue>(
    () => ({
      isDarkMode,
      palette,
      activeObjectId,
      onObjectClick: obj => setActiveObjectId(obj.id),
    }),
    [isDarkMode, palette, activeObjectId],
  );

  const handleAddSubfolder = useCallback(
    (parentFolder: Folder) => {
      handleAddFolder({
        parentId: parentFolder.id,
        spaceId: parentFolder.space || undefined,
      });
    },
    [handleAddFolder],
  );

  const itemHandlers = useMemo<Omit<ItemsSectionProps, 'items' | 'spaceId'>>(
    () => ({
      onObjectEdit: handleObjectEdit,
      onObjectDelete: handleObjectDelete,
      onObjectAdd: handleAddObject,
      onVectorEdit: ns => setEditingVector(ns),
      onVectorDelete: ns => setPendingVectorNamespace(ns),
      onVectorAdd: handleAddVector,
      onFolderRename: handleFolderRename,
      onFolderDelete: folderActions.handleFolderDelete,
      onAddSubfolder: handleAddSubfolder,
      onAddItem: handleAddItem,
      ...handlers,
    }),
    [
      handleObjectEdit,
      handleObjectDelete,
      handleAddObject,
      handleAddVector,
      handleFolderRename,
      folderActions.handleFolderDelete,
      handleAddSubfolder,
      handleAddItem,
      handlers,
    ],
  );

  return (
    <DataSidebarContext.Provider value={contextValue}>
      <DataSidebarModals
        spaces={spaces}
        isDarkMode={isDarkMode}
        modals={modals}
        dispatch={dispatch}
        renameModal={renameModal}
        folderActions={folderActions}
        deleteVectorMemory={deleteVectorMemory}
        refresh={refresh}
        resetObjectUpdate={resetObjectUpdate}
        setAuthUser={setAuthUser}
        createItemRequest={createItemRequest}
        setCreateItemRequest={setCreateItemRequest}
        vectorCreateRequest={vectorCreateRequest}
        setVectorCreateRequest={setVectorCreateRequest}
        editingObject={editingObject}
        setEditingObject={setEditingObject}
        editingVector={editingVector}
        setEditingVector={setEditingVector}
        pendingVectorNamespace={pendingVectorNamespace}
        setPendingVectorNamespace={setPendingVectorNamespace}
      />

      <DragDropProvider>
        <Box
          ref={setScrollRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {searchResults ? (
            <ItemsSection items={searchResults} {...itemHandlers} />
          ) : (
            <>
              <Section
                title='Private'
                hasBorder={spaces.length > 0}
                isAccordion
                defaultExpanded
                onAddFolder={() => handleAddFolder({})}
              >
                <UploadItems />
                <ItemsSection
                  items={privateItems}
                  customFolders={enrichedPrivateFolders}
                  {...itemHandlers}
                />
              </Section>

              {sortedSpaces.map(({ space, items }, index) => {
                const isEmptySpace =
                  items.objects.length === 0 &&
                  items.vectorMemory.length === 0 &&
                  items.generatedImages.length === 0 &&
                  items.generatedAudio.length === 0 &&
                  items.generatedFiles.length === 0;

                const spaceCustomFolders = enrichFoldersWithItems(
                  space.folder ?? [],
                  items.objects,
                  items.vectorMemory,
                  space.id,
                );

                return (
                  <Section
                    key={space.id}
                    title={space.name}
                    spaceId={space.id}
                    hasBorder={index < sortedSpaces.length - 1}
                    isAccordion
                    isDisabled={!!space.disabled}
                    onAddFolder={() => handleAddFolder({ spaceId: space.id })}
                    onEditSpace={() => handleEditSpace(space.id)}
                    onArchive={() => handleArchiveSpace(space.id)}
                  >
                    <UploadItems spaceId={space.id} />
                    {isEmptySpace && (
                      <Typography
                        sx={{
                          color: isDarkMode
                            ? palette.gray[500]
                            : palette.gray[400],
                          fontFamily: 'Roboto',
                          fontSize: '12px',
                          fontStyle: 'italic',
                          padding: '8px',
                        }}
                      >
                        No items found
                      </Typography>
                    )}
                    <ItemsSection
                      items={items}
                      spaceId={space.id}
                      customFolders={spaceCustomFolders}
                      {...itemHandlers}
                    />
                  </Section>
                );
              })}
            </>
          )}
        </Box>
      </DragDropProvider>
    </DataSidebarContext.Provider>
  );
};
